import React, { useMemo, useState } from 'react';
import { ButtonBar, Button, Dialog } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { useParams } from 'react-router';
import { ConfiguratorOptionDto } from '@/services/configurator/configurator.types';
import { useOptionConfigurator } from '../OptionConfiguratorContext';
import { useConfiguratorMutations } from '../ConfiguratorMutationContext';
import { findDependentGroups } from '../dependencyHelpers';
import { ImmersiveDependencyWizardStep } from './ImmersiveDependencyWizardStep';
import theme from './ImmersiveDependencyWizardModal.module.scss';

export interface ImmersiveDependencyWizardModalProps {
    triggerOption: ConfiguratorOptionDto;
    onClose: () => void;
}

/**
 * Dependency wizard styled for the immersive configurator: a compact, viewport-fitting dialog whose
 * options reuse the immersive option-menu card (via `BuyerOptionCard`'s `compactSelection`), split
 * into valid / incompatible sections. The non-immersive buyer flow keeps the plain
 * `DependencyWizardModal`; `useOptionSelection` picks between them.
 */
export const ImmersiveDependencyWizardModal: React.FC<ImmersiveDependencyWizardModalProps> = ({
    triggerOption,
    onClose,
}) => {
    const { configuratorData } = useOptionConfigurator();
    const { selectOptions } = useConfiguratorMutations();
    const { roomId } = useParams<{ roomId: string }>();

    const allGroups = useMemo(() => configuratorData?.groups ?? [], [configuratorData]);

    const triggerGroup = useMemo(
        () => allGroups.find((x) => x.roomId === +roomId && x.id === triggerOption.optionGroupId),
        [allGroups, roomId, triggerOption.optionGroupId]
    );

    const dependentGroups = useMemo(
        () => (triggerGroup ? findDependentGroups(allGroups, triggerGroup) : []),
        [allGroups, triggerGroup]
    );

    const [pendingSelections, setPendingSelections] = useState<Record<number, ConfiguratorOptionDto>>(() => {
        const initial: Record<number, ConfiguratorOptionDto> = {};
        dependentGroups.forEach((g) => {
            if (g.selectedOption) {
                initial[g.id] = g.selectedOption;
            }
        });
        return initial;
    });

    const [currentStepIndex, setCurrentStepIndex] = useState(0);

    const currentGroup = dependentGroups[currentStepIndex];
    const isLastStep = currentStepIndex === dependentGroups.length - 1;

    // Single-column layout: keep the dialog narrow and stable across steps.
    const contentWidth = 'sm';

    // Only include the trigger option + selections from steps already passed by the user.
    // Pre-populated selections from future steps must NOT be used as constraints.
    const knownSelections = useMemo<Record<number, ConfiguratorOptionDto>>(
        () => ({
            ...(triggerGroup ? { [triggerGroup.id]: triggerOption } : {}),
            ...Object.fromEntries(
                dependentGroups
                    .slice(0, currentStepIndex)
                    .flatMap((g) => (pendingSelections[g.id] ? [[g.id, pendingSelections[g.id]]] : []))
            ),
        }),
        [triggerGroup, triggerOption, dependentGroups, currentStepIndex, pendingSelections]
    );

    const handleSelect = (option: ConfiguratorOptionDto) => {
        setPendingSelections((prev) => ({ ...prev, [currentGroup.id]: option }));
    };

    const handleNext = () => {
        setCurrentStepIndex((i) => i + 1);
    };

    const handleBack = () => {
        setCurrentStepIndex((i) => i - 1);
    };

    // Defer every close to a microtask so it runs AFTER the triggering click finishes. Unmounting the
    // dialog synchronously inside a click makes rubus re-assert (or the click fall through to the
    // option card beneath), reopening it — so Cancel/Escape/outside-click would otherwise not close.
    const close = () => queueMicrotask(onClose);

    const handleConfirm = () => {
        const resolvedRoomId = roomId === '0' || !roomId ? null : Number(roomId);
        const selections = [
            { option: triggerOption, customFieldData: null, roomId: resolvedRoomId },
            ...dependentGroups
                .map((g) => pendingSelections[g.id])
                .filter((opt): opt is ConfiguratorOptionDto => !!opt)
                .map((option) => ({ option, customFieldData: null, roomId: resolvedRoomId })),
        ];
        // Defer to a microtask so the close runs AFTER this click event finishes: unmounting the
        // dialog synchronously inside the click makes it reopen (rubus re-asserts / the click falls
        // through to the card beneath). Within that microtask, close BEFORE mutating — this dialog is
        // mounted inside an option card in the list `selectOptions` re-renders, so mutating while
        // still mounted caught the card/modal in that reconciliation and flashed. `selectOptions`
        // patches the cache optimistically, so the view is already correct once the dialog is gone.
        queueMicrotask(() => {
            onClose();
            void selectOptions(selections);
        });
    };

    if (!triggerGroup || !currentGroup) return null;

    const pendingForCurrent = pendingSelections[currentGroup.id];
    const currentSelectionIsInvalid =
        pendingForCurrent !== undefined &&
        pendingForCurrent.dependencyConflicts.some((fw) => knownSelections[fw.groupId]?.id === fw.optionId);

    return (
        <Dialog
            open
            heightLimit="window"
            onOpenChange={(open) => {
                if (!open) close();
            }}>
            <Dialog.Content contentWidth={contentWidth} className={theme.content}>
                <Dialog.Header>
                    <div className={theme.header}>
                        <span>{currentGroup.name}</span>
                        {dependentGroups.length > 1 && (
                            <span className={theme.stepCount}>
                                {currentStepIndex + 1} / {dependentGroups.length}
                            </span>
                        )}
                    </div>
                </Dialog.Header>
                <ImmersiveDependencyWizardStep
                    group={currentGroup}
                    triggerGroupName={triggerGroup.name}
                    triggerOptionName={triggerOption.productName}
                    knownSelections={knownSelections}
                    pendingOption={pendingSelections[currentGroup.id] ?? null}
                    onSelect={handleSelect}
                />
                <ButtonBar margins="none" className={theme.footer}>
                    {currentStepIndex > 0 && (
                        <Button variant="tertiary" onClick={handleBack}>
                            <Trans>Back</Trans>
                        </Button>
                    )}
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={close}>
                            <Trans>Cancel</Trans>
                        </Button>
                        {isLastStep ? (
                            <Button
                                variant="primary"
                                onClick={handleConfirm}
                                disabled={!pendingForCurrent || currentSelectionIsInvalid}>
                                <Trans>Confirm</Trans>
                            </Button>
                        ) : (
                            <Button
                                variant="primary"
                                onClick={handleNext}
                                disabled={!pendingForCurrent || currentSelectionIsInvalid}>
                                <Trans>Next</Trans>
                            </Button>
                        )}
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};
